import { z } from 'zod';

export const ProductConfigurationSchema = z.object({
    index: z.number(),
    productType: z.string().min(1),
    category: z.string().min(1),
    isMainProduct: z.boolean(),
    productName: z.string().min(1),
    quantity: z.number(),
    unit: z.string().min(1),
    purchasePrice: z.number(),
    purchaseTaxClassId: z.string().min(1),
    salesPrice: z.number(),
    salesTaxClassId: z.string().min(1),
    currencyCode: z.string().min(1),
    showZeroPurchasePrice: z.boolean(),
    showZeroSalesPrice: z.boolean(),
    processingType: z.string().min(1),
    additionalProperties: z.object({
        schemaId: z.string().min(1),
        name: z.string().min(1),
        disposalPurpose: z.string().optional(),
    }),
});

export const PositionTemplateSchema = z.object({
    name: z.string().min(1),
    productConfigurations: z.array(ProductConfigurationSchema).min(1),
});

export const PositionTemplatesArraySchema = z.array(PositionTemplateSchema).min(1);

export interface PositionTemplate {
    name: string;
    productConfigurations: ProductConfiguration[];
}

export interface ProductConfiguration {
    index: number;
    productType: string;
    category: string;
    isMainProduct: boolean;
    productName: string;
    quantity: number;
    unit: string;
    purchasePrice: number;
    purchaseTaxClassId: string;
    salesPrice: number;
    salesTaxClassId: string;
    currencyCode: string;
    showZeroPurchasePrice: boolean;
    showZeroSalesPrice: boolean;
    processingType: string;
    additionalProperties: {
        schemaId: string;
        name: string;
        disposalPurpose?: string;
    };
}

export interface Preset {
    name: string;
    partnerOrganizationId: number | null;
    positionTemplates: PositionTemplate[];
}

export const AACHEN_PRESET: Preset = {
    name: 'Aachen',
    partnerOrganizationId: 9997,
    positionTemplates: [
        {
            name: '170107 Bauschutt Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_RECYCLING',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170107 Bauschutt Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 9.9,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170107 Bauschutt Selbstanlieferung',
                        disposalPurpose: 'RECYCLING',
                    },
                },
            ],
        },
        {
            name: '170504 Boden und Steine Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_RECYCLING',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170504 Boden und Steine Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 19.5,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170504 Boden und Steine Selbstanlieferung',
                        disposalPurpose: 'RECYCLING',
                    },
                },
            ],
        },
        {
            name: '170904 Baumischabfall / 200307 Sperrmüll Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_RECYCLING',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170904 Baumischabfall / 200307 Sperrmüll Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 245,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170904 Baumischabfall / 200307 Sperrmüll Selbstanlieferung',
                        disposalPurpose: 'RECYCLING',
                    },
                },
            ],
        },
        {
            name: '200201 Grünschnitt Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_DISPOSAL',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '200201 Grünschnitt Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 70,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '200201 Grünschnitt Selbstanlieferung',
                        disposalPurpose: 'DISPOSAL',
                    },
                },
            ],
        },
        {
            name: '170201 Holz A I - A III Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_RECYCLING',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170201 Holz A I - A III Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 65.5,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170201 Holz A I - A III Selbstanlieferung',
                        disposalPurpose: 'RECYCLING',
                    },
                },
            ],
        },
        {
            name: '170504 Boden und Steine (Verunreinigt mit Grasnarbe oder Wurzeln) Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_RECYCLING',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170504 Boden und Steine (Verunreinigt mit Grasnarbe oder Wurzeln) Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 34,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170504 Boden und Steine (Verunreinigt mit Grasnarbe oder Wurzeln) Selbstanlieferung',
                        disposalPurpose: 'RECYCLING',
                    },
                },
            ],
        },
        {
            name: '170802 Rigips (Baustoffe auf Gipsbasis) Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_DISPOSAL',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170802 Rigips (Baustoffe auf Gipsbasis) Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 97,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170802 Rigips (Baustoffe auf Gipsbasis) Selbstanlieferung',
                        disposalPurpose: 'DISPOSAL',
                    },
                },
            ],
        },
        {
            name: '170107 Leichtbaustoffe (KS - Bimsbauschutt) Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_RECYCLING',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170107 Leichtbaustoffe (KS - Bimsbauschutt) Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 30,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170107 Leichtbaustoffe (KS - Bimsbauschutt) Selbstanlieferung',
                        disposalPurpose: 'RECYCLING',
                    },
                },
            ],
        },
        {
            name: '150102 Verpackungen aus Kunststoff (Styropor / Folien)',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_DISPOSAL',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '150102 Verpackungen aus Kunststoff (Styropor / Folien)',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 650,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '150102 Verpackungen aus Kunststoff (Styropor / Folien)',
                        disposalPurpose: 'DISPOSAL',
                    },
                },
            ],
        },
        {
            name: '170101 Beton Sortenrein (Kantenlänge 60 x 60 cm) Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_RECYCLING',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170101 Beton Sortenrein (Kantenlänge 60 x 60 cm) Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 5,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170101 Beton Sortenrein (Kantenlänge 60 x 60 cm) Selbstanlieferung',
                        disposalPurpose: 'RECYCLING',
                    },
                },
            ],
        },
        {
            name: '170107 Boden / Bauschutt Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_RECYCLING',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170107 Boden / Bauschutt Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 19.5,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170107 Boden / Bauschutt Selbstanlieferung',
                        disposalPurpose: 'RECYCLING',
                    },
                },
            ],
        },
        {
            name: '170802 Ytong / Porengasbeton Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_DISPOSAL',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170802 Ytong / Porengasbeton Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 73,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170802 Ytong / Porengasbeton Selbstanlieferung',
                        disposalPurpose: 'DISPOSAL',
                    },
                },
            ],
        },
        {
            name: '170302 Bit. Straßenaufbruch Verwertungsklasse A Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_RECYCLING',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170302 Bit. Straßenaufbruch Verwertungsklasse A Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 25,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170302 Bit. Straßenaufbruch Verwertungsklasse A Selbstanlieferung',
                        disposalPurpose: 'RECYCLING',
                    },
                },
            ],
        },
        {
            name: '170302 Bit. Straßenaufbruch Verwertungsklasse B Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_DISPOSAL',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170302 Bit. Straßenaufbruch Verwertungsklasse B Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 50,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170302 Bit. Straßenaufbruch Verwertungsklasse B Selbstanlieferung',
                        disposalPurpose: 'DISPOSAL',
                    },
                },
            ],
        },
        {
            name: '200201 Wurzelwerk Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_RECYCLING',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '200201 Wurzelwerk Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 80,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '200201 Wurzelwerk Selbstanlieferung',
                        disposalPurpose: 'RECYCLING',
                    },
                },
            ],
        },
        {
            name: 'RC I 0/45 mm Selbstabholung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_RECYCLED_BULK_MATERIAL',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: 'RC I 0/45 mm Selbstabholung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 7,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_MATERIAL_V1',
                        name: 'RC I 0/45 mm Selbstabholung',
                    },
                },
            ],
        },
        {
            name: 'RC Vorabsiebung Selbstabholung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_RECYCLED_BULK_MATERIAL',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: 'RC Vorabsiebung Selbstabholung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 1,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_MATERIAL_V1',
                        name: 'RC Vorabsiebung Selbstabholung',
                    },
                },
            ],
        },
        {
            name: 'Mutterboden (maschinell aufbereitet) Selbstabholung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_BULK_MATERIAL',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: 'Mutterboden (maschinell aufbereitet) Selbstabholung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 14.9,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_MATERIAL_V1',
                        name: 'Mutterboden (maschinell aufbereitet) Selbstabholung',
                    },
                },
            ],
        },
        {
            name: 'Basaltsplitt 2/8 mm Selbstabholung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_BULK_MATERIAL',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: 'Basaltsplitt 2/8 mm Selbstabholung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 45.9,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_MATERIAL_V1',
                        name: 'Basaltsplitt 2/8 mm Selbstabholung',
                    },
                },
            ],
        },
        {
            name: 'Basaltsplitt 16/32 mm Selbstabholung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_BULK_MATERIAL',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: 'Basaltsplitt 16/32 mm Selbstabholung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 48.9,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_MATERIAL_V1',
                        name: 'Basaltsplitt 16/32 mm Selbstabholung',
                    },
                },
            ],
        },
        {
            name: 'Kalkstein 0/45 mm FSS Selbstabholung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_BULK_MATERIAL',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: 'Kalkstein 0/45 mm FSS Selbstabholung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 22.5,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_MATERIAL_V1',
                        name: 'Kalkstein 0/45 mm FSS Selbstabholung',
                    },
                },
            ],
        },
        {
            name: 'Kalkstein Grobschlag 60/120 mm Selbstabholung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_BULK_MATERIAL',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: 'Kalkstein Grobschlag 60/120 mm Selbstabholung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 29.5,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_MATERIAL_V1',
                        name: 'Kalkstein Grobschlag 60/120 mm Selbstabholung',
                    },
                },
            ],
        },
        {
            name: 'Abdecksand 0/4 mm Selbstabholung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_BULK_MATERIAL',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: 'Abdecksand 0/4 mm Selbstabholung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 21,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_MATERIAL_V1',
                        name: 'Abdecksand 0/4 mm Selbstabholung',
                    },
                },
            ],
        },
        {
            name: '170107 Bauschutt (Übergröße) Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_RECYCLING',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170107 Bauschutt (Übergröße) Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 14.5,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170107 Bauschutt (Übergröße) Selbstanlieferung',
                        disposalPurpose: 'RECYCLING',
                    },
                },
            ],
        },
        {
            name: '170101 Beton (Übergröße) Selbstanlieferung',
            productConfigurations: [
                {
                    index: 1,
                    productType: 'CUSTOM_NON_HAZARDOUS_WASTE_RECYCLING',
                    category: 'MATERIAL',
                    isMainProduct: true,
                    productName: '170101 Beton (Übergröße) Selbstanlieferung',
                    quantity: 1,
                    unit: 'TON',
                    purchasePrice: 0,
                    purchaseTaxClassId: 'S-DE',
                    salesPrice: 9.5,
                    salesTaxClassId: 'S-DE',
                    currencyCode: 'EUR',
                    showZeroPurchasePrice: true,
                    showZeroSalesPrice: false,
                    processingType: 'MANUAL',
                    additionalProperties: {
                        schemaId: 'MANUAL_WASTE_MATERIAL_V1',
                        name: '170101 Beton (Übergröße) Selbstanlieferung',
                        disposalPurpose: 'RECYCLING',
                    },
                },
            ],
        },
    ],
};
